import React, { useMemo, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import FolderIcon from '@mui/icons-material/Folder'
import HomeIcon from '@mui/icons-material/Home'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'

/**
 * Sélecteur de dossier cible pour « Déplacer vers… » / « Copier vers… »
 * (explorateur de documents). Affiche l'arborescence complète à partir de la
 * liste PLATE des dossiers (documents-tree/) et rend non-sélectionnables les
 * sous-arbres exclus (un dossier ne peut pas être déplacé dans lui-même).
 *
 * Props :
 *  - folders      : liste plate [{id, Nom, parent, ...}]
 *  - excludeIds   : ids de dossiers dont tout le sous-arbre est désactivé
 *  - title        : titre du dialogue (« Déplacer vers… », « Copier vers… »)
 *  - confirmLabel : libellé du bouton de validation
 *  - onConfirm    : (targetId|null) => void — null = racine
 *  - onClose      : fermeture sans action
 */
export default function FolderPickerDialog({
  folders,
  excludeIds = [],
  title,
  confirmLabel,
  onConfirm,
  onClose,
}) {
  // undefined = rien de choisi, null = racine, nombre = dossier
  const [target, setTarget] = useState(undefined)
  const [expanded, setExpanded] = useState(() => new Set())

  // parent -> enfants, pour dérouler l'arbre depuis la liste plate
  const childrenOf = useMemo(() => {
    const map = new Map()
    folders.forEach((f) => {
      const key = f.parent ?? 'root'
      if (!map.has(key)) map.set(key, [])
      map.get(key).push(f)
    })
    return map
  }, [folders])

  // Sous-arbres exclus : les dossiers déplacés + tous leurs descendants
  const disabledIds = useMemo(() => {
    const disabled = new Set(excludeIds)
    // propagation aux descendants : on répète tant qu'un dossier est ajouté
    // (profondeur max = nb de dossiers, en pratique quelques itérations)
    for (let pass = 0; pass < folders.length; pass += 1) {
      const before = disabled.size
      folders.forEach((f) => {
        if (f.parent != null && disabled.has(f.parent)) disabled.add(f.id)
      })
      if (disabled.size === before) break
    }
    return disabled
  }, [folders, excludeIds])

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const renderRow = (folder, depth) => {
    const children = childrenOf.get(folder.id) || []
    const disabled = disabledIds.has(folder.id)
    const selected = target === folder.id
    return (
      <React.Fragment key={folder.id}>
        <Box
          onClick={() => !disabled && setTarget(folder.id)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pl: 1 + depth * 2.5,
            pr: 1,
            py: 0.75,
            borderRadius: 1,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.4 : 1,
            bgcolor: selected ? '#e3f0fb' : 'transparent',
            '&:hover': { bgcolor: selected ? '#e3f0fb' : disabled ? 'transparent' : '#f4f6f8' },
          }}
        >
          {children.length > 0 ? (
            <Box
              onClick={(e) => {
                e.stopPropagation()
                toggleExpand(folder.id)
              }}
              sx={{ display: 'flex', cursor: 'pointer', color: '#666' }}
            >
              {expanded.has(folder.id) ? (
                <KeyboardArrowDownIcon fontSize="small" />
              ) : (
                <KeyboardArrowRightIcon fontSize="small" />
              )}
            </Box>
          ) : (
            <Box sx={{ width: 20 }} />
          )}
          <FolderIcon sx={{ color: '#f8ce46' }} fontSize="small" />
          <Box sx={{ fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {folder.Nom}
          </Box>
        </Box>
        {expanded.has(folder.id) &&
          children.map((child) => renderRow(child, depth + 1))}
      </React.Fragment>
    )
  }

  const roots = childrenOf.get('root') || []

  return (
    <Dialog fullWidth maxWidth="xs" open onClose={onClose}>
      <DialogTitle sx={{ color: 'black !important' }}>{title}</DialogTitle>
      <DialogContent dividers sx={{ maxHeight: 380, p: 1 }}>
        <Box
          onClick={() => setTarget(null)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1,
            py: 0.75,
            borderRadius: 1,
            cursor: 'pointer',
            bgcolor: target === null ? '#e3f0fb' : 'transparent',
            '&:hover': { bgcolor: target === null ? '#e3f0fb' : '#f4f6f8' },
          }}
        >
          <Box sx={{ width: 20 }} />
          <HomeIcon sx={{ color: '#4a92d3' }} fontSize="small" />
          <Box sx={{ fontSize: 14, fontWeight: 600 }}>Mes Documents (racine)</Box>
        </Box>
        {roots.map((folder) => renderRow(folder, 0))}
      </DialogContent>
      <DialogActions>
        <Button disabled={target === undefined} onClick={() => onConfirm(target)}>
          {confirmLabel}
        </Button>
        <Button onClick={onClose}>Annuler</Button>
      </DialogActions>
    </Dialog>
  )
}
